import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { APP_PALETTE } from "../../theme/palette";
import { deliverySchedulesApi } from "../../services/api";
import { summarizeItems, sumItems, toDateIso } from "../../utils/logistics";
import DeliverySlip from "./DeliverySlip";
import PrintIcon from "@mui/icons-material/Print";
import { normalizeLocationLabel, normalizeTransportCompanies } from "../../utils/logistics";
import { emitDeliverySchedulesUpdated, useDeliverySchedulesUpdates } from "../../hooks/useDeliverySchedulesUpdates";
import { formatCurrencyBs } from "../../utils/formatters";

const groupSchedulesByClient = (schedules, orderById) => {
    const groups = {};
    for (const schedule of schedules) {
      const order = orderById.get(schedule.order_id);
      const clientId = order?.client_id;
      if (!clientId) {
        groups[`solo-${schedule.id}`] = [schedule];
        continue;
      }
      if (!groups[clientId]) groups[clientId] = [];
      groups[clientId].push(schedule);
    }
    return Object.values(groups);
  };

export default function DeliveriesTodayPanel({ orders = [], onUpdate, brandLogoUrl }) {
  const [todaySchedules, setTodaySchedules] = useState([]);
  const [manageDialog, setManageDialog] = useState({ open: false, schedule: null });
  const [slipDialog, setSlipDialog] = useState({ open: false, schedule: null });
  const [note, setNote] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [location, setLocation] = useState("");
  const [transportCompanies, setTransportCompanies] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [manageFns, setManageFns] = useState({ delivered: null, notDelivered: null });

  const loadToday = async () => {
    const res = await deliverySchedulesApi.listToday();
    setTodaySchedules(res.data || []);
  };

  useEffect(() => {
    loadToday();
  }, []);

  useDeliverySchedulesUpdates(loadToday);

  const orderById = useMemo(() => {
    return new Map(orders.map((order) => [order.id, order]));
  }, [orders]);

  const groupedSchedules = useMemo(
    () => groupSchedulesByClient(todaySchedules, orderById),
    [todaySchedules, orderById]
  );

  const openManage = (schedule, onDelivered, onNotDelivered) => {
    setManageDialog({ open: true, schedule });
    setManageFns({ delivered: onDelivered, notDelivered: onNotDelivered });
    setNote(schedule.notes || "");
    setRescheduleDate("");
    if (schedule.delivery_mode === "other_city") {
      setLocation(schedule.destination_city || "");
    } else {
      setLocation(schedule.location || schedule.delivery_location || "");
    }
    setTransportCompanies(normalizeTransportCompanies(schedule.transport_companies).join(", "));
  };

  const closeManage = () => setManageDialog({ open: false, schedule: null });

  const openSlip = (schedule) => {
    setSlipDialog({ open: true, schedule });
  };

  const closeSlip = () => setSlipDialog({ open: false, schedule: null });

  const refresh = async () => {
    await loadToday();
    if (onUpdate) onUpdate();
    emitDeliverySchedulesUpdated();
  };

  const markDelivered = async () => {
    if (!manageDialog.schedule) return;
    await syncLocationIfChanged();
    if (manageFns.delivered) await manageFns.delivered();
    else await deliverySchedulesApi.markDelivered(manageDialog.schedule.id, { notes: note || undefined });
    closeManage();
    await refresh();
  };

  const markNotDelivered = async () => {
    if (!manageDialog.schedule) return;
    await syncLocationIfChanged();
    if (manageFns.notDelivered) await manageFns.notDelivered();
    else await deliverySchedulesApi.markNotDelivered(manageDialog.schedule.id, { notes: note || undefined });
    closeManage();
    await refresh();
  };

  const handleReschedule = async () => {
    if (!manageDialog.schedule || !rescheduleDate) return;
    await syncLocationIfChanged();
    await deliverySchedulesApi.reschedule(manageDialog.schedule.id, { new_date: rescheduleDate, notes: note || undefined });
    closeManage();
    await refresh();
  };

  const saveChanges = async () => {
    if (!manageDialog.schedule) return;
    await syncLocationIfChanged();
    if (rescheduleDate) {
      await deliverySchedulesApi.reschedule(manageDialog.schedule.id, { new_date: rescheduleDate, notes: note || undefined });
    }
    closeManage();
    await refresh();
  };

  const syncLocationIfChanged = async () => {
      if (!manageDialog.schedule) return;

      const nextTransportCompanies = normalizeTransportCompanies(transportCompanies);
      const nextDeliveryMode = nextTransportCompanies.length > 0 ? "other_city" : "same_city";
      const next = String(location || "").trim();

      const payload = {
        delivery_mode: nextDeliveryMode,
        transport_companies: nextTransportCompanies,
      };

      if (nextDeliveryMode === "other_city") {
        payload.destination_city = next || null;
        payload.location = null;
        payload.delivery_location = [
          "Otra ciudad/departamento",
          next,
          nextTransportCompanies.length > 0 ? `Transporte: ${nextTransportCompanies.join(", ")}` : null,
        ].filter(Boolean).join(" - ");
      } else {
        payload.location = next || null;
        payload.destination_city = null;
        payload.delivery_location = next || null;
      }

      await deliverySchedulesApi.updateLocation(manageDialog.schedule.id, payload);
  };

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.04)" }}>
      <Typography variant="h6" fontWeight={700} color={APP_PALETTE.text.primary} mb={0.5}>
        Entregas para hoy
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Revisa las entregas agendadas para hoy, con cliente, teléfono, detalle y notas.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {todaySchedules.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No hay entregas programadas para hoy
          </Typography>
        )}

        {groupedSchedules.map((group) => {
          const firstSchedule = group[0];
          const order = orderById.get(firstSchedule.order_id);
          const client = order?.client;
          const isGroup = group.length > 1;
          const groupKey = client?.id ? `client-${client.id}` : `solo-${firstSchedule.id}`;
          const isExpanded = expandedGroups.has(groupKey);

          const totalItems = group.reduce((acc, s) => {
            const o = orderById.get(s.order_id);
            return acc + sumItems(o);
          }, 0);

          const totalAmount = group.reduce((acc, s) => {
            const o = orderById.get(s.order_id);
            return acc + (Number(o?.total) || 0);
          }, 0);

          const allItemSummaries = group.flatMap((s) => {
            const o = orderById.get(s.order_id);
            return o?.items?.map((it) => ({
              name: it.product?.name || `Producto #${it.product_id}`,
              quantity: it.quantity,
              orderId: o.id,
            })) || [];
          });

          const handleMarkDelivered = async () => {
            await Promise.all(
              group.map((s) =>
                deliverySchedulesApi.markDelivered(s.id, { notes: note || undefined })
              )
            );
            await refresh();
          };

          const handleMarkNotDelivered = async () => {
            await Promise.all(
              group.map((s) =>
                deliverySchedulesApi.markNotDelivered(s.id, { notes: note || undefined })
              )
            );
            await refresh();
          };

          return (
            <Box
              key={groupKey}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${APP_PALETTE.surfaces.border}`,
                background: APP_PALETTE.surfaces.subtle,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: { xs: "stretch", sm: "flex-start" }, flexDirection: { xs: "column", sm: "row" } }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                    <Typography fontWeight={700}>
                      {client?.full_name || `Pedido #${firstSchedule.order_id}`}
                    </Typography>
                    {isGroup && (
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          background: "primary.main",
                          bgcolor: "primary.main",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                        onClick={() =>
                          setExpandedGroups((prev) => {
                            const copy = new Set(prev);
                            copy.has(groupKey) ? copy.delete(groupKey) : copy.add(groupKey);
                            return copy;
                          })
                        }
                      >
                        <Typography fontSize={11} fontWeight={700} color="white">
                          {group.length} pedidos · {isExpanded ? "▲ ocultar" : "▼ ver"}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" display="block">
                    {client?.phone || "Sin teléfono"} · {totalItems} prenda(s) · {formatCurrencyBs(totalAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Destino: {normalizeLocationLabel(firstSchedule.delivery_location)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Transportes: {normalizeTransportCompanies(firstSchedule.transport_companies).join(", ") || "-"}
                  </Typography>
                  {firstSchedule.notes && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Notas: {firstSchedule.notes}
                    </Typography>
                  )}

                  {isGroup && isExpanded && (
                    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                      {group.map((s) => {
                        const o = orderById.get(s.order_id);
                        return (
                          <Box
                            key={s.id}
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              background: "background.paper",
                            }}
                          >
                            <Typography fontSize={12} fontWeight={700}>
                              Pedido #{o?.id}
                            </Typography>
                            <Typography fontSize={12} color="text.secondary">
                              {sumItems(o)} prenda(s) · {formatCurrencyBs(o?.total)}
                            </Typography>
                            {o?.items?.map((it) => (
                              <Typography key={it.id || it.product_id} fontSize={11} color="text.secondary">
                                · {it.product?.name || `Producto #${it.product_id}`} x{it.quantity}
                              </Typography>
                            ))}
                          </Box>
                        );
                      })}
                      <Typography fontSize={11} color="text.secondary" sx={{ pt: 0.5, borderTop: "1px solid", borderColor: "divider" }}>
                        Total combinado: {totalItems} prendas · {formatCurrencyBs(totalAmount)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexDirection: { xs: "column", sm: "row" }, width: { xs: "100%", sm: "auto" } }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={() => openSlip(firstSchedule)}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Imprimir
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => openManage(firstSchedule, handleMarkDelivered, handleMarkNotDelivered)} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    Gestionar
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Dialog open={manageDialog.open} onClose={closeManage} fullWidth maxWidth="sm">
        <DialogTitle>Gestionar entrega de hoy</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Marca si se entregó, si no se pudo entregar, o cambia la fecha si el cliente pidió reprogramar.
          </DialogContentText>
          <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
            <TextField
              size="small"
              label="Lugar de entrega"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              helperText="Puedes cambiar ciudad, zona o dirección hasta último momento."
            />
            <TextField
              size="small"
              label="Empresas de transporte"
              value={transportCompanies}
              onChange={(e) => setTransportCompanies(e.target.value)}
              helperText="Separa varias empresas con coma."
            />
            <TextField size="small" label="Notas" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} />
            <TextField
              size="small"
              type="date"
              label="Reprogramar para"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 1 }}>
          <Button onClick={closeManage} sx={{ width: { xs: "100%", sm: "auto" } }}>Cerrar</Button>
          <Button variant="outlined" onClick={saveChanges} sx={{ width: { xs: "100%", sm: "auto" } }}>
            Guardar cambios
          </Button>
          <Button color="error" onClick={markNotDelivered} sx={{ width: { xs: "100%", sm: "auto" } }}>
            No entregado
          </Button>
          <Button variant="contained" color="primary" onClick={markDelivered} sx={{ width: { xs: "100%", sm: "auto" } }}>
            Entregado
          </Button>
        </DialogActions>
      </Dialog>

      <DeliverySlip
        open={slipDialog.open}
        onClose={closeSlip}
        delivery={slipDialog.schedule}
        order={slipDialog.schedule ? orderById.get(slipDialog.schedule.order_id) : null}
        client={slipDialog.schedule && orderById.get(slipDialog.schedule.order_id) ? orderById.get(slipDialog.schedule.order_id).client : null}
        brandLogoUrl={brandLogoUrl}
      />
    </Paper>
  );
}
